/**
 * Tracks in-flight task creations so mutations aimed at a temp- id can wait
 * for the real id instead of hitting /projects/:id/tasks/temp-… (which 404s).
 *
 * Anything that POSTs a task under an optimistic `temp-…` row registers the
 * request here; taskApi's mutations resolve the temp id through it.
 * Settled entries stay for the session (tiny, and stale callers may still
 * hold the temp id after the row was confirmed).
 */

import type { Task } from './types';

const creations = new Map<string, Promise<Task | null>>();

export function isTempTaskId(id: string): boolean {
  return id.startsWith('temp-');
}

/** Register a create request. `saved` must resolve to null (never reject) when the create failed. */
export function registerTaskCreation(tempId: string, saved: Promise<Task | null>): void {
  creations.set(tempId, saved);
}

/**
 * Deferred form of registerTaskCreation for callers that reconcile their own
 * state: call the returned function with the saved task (or null on failure)
 * once the temp row has been replaced/removed.
 */
export function trackTaskCreation(tempId: string): (saved: Task | null) => void {
  let settle!: (saved: Task | null) => void;
  registerTaskCreation(tempId, new Promise<Task | null>((resolve) => (settle = resolve)));
  return settle;
}

/**
 * Resolve a temp id to its saved task. `undefined` = no create is tracked for
 * this id (nothing to wait for); `null` = the create failed.
 */
export function resolveTaskCreation(tempId: string): Promise<Task | null> | undefined {
  return creations.get(tempId);
}

/** Test helper */
export function clearTaskCreations(): void {
  creations.clear();
}
